import { useEffect, useState } from "react";
import { AppShell } from "../components/AppShell";
import { api, apiError } from "../lib/apiClient";
import { toast } from "sonner";
import {
  BrainCircuit, Ruler, MapPin, ShoppingBag, Plus, Trash2, Star, Loader2, Sparkles,
} from "lucide-react";

const CATEGORIES = [
  { id: "taille", label: "Tailles & mesures", icon: Ruler, hint: "ex. haut = M · pointure = 42 · tour de taille = 82 cm" },
  { id: "habitude", label: "Habitudes d'achat", icon: ShoppingBag, hint: "ex. marques = Nike, Adidas · budget max = 80 € · régime = sans lactose" },
];
const ADDRESS_FIELDS = [
  { id: "full_name", label: "Nom complet", placeholder: "Jean Dupont" },
  { id: "line1", label: "Adresse", placeholder: "5 rue de la République" },
  { id: "line2", label: "Complément", placeholder: "Bât. B, 3e étage" },
  { id: "zip_code", label: "Code postal", placeholder: "69001" },
  { id: "city", label: "Ville", placeholder: "Lyon" },
  { id: "country", label: "Pays", placeholder: "France" },
  { id: "phone", label: "Téléphone", placeholder: "06 12 34 56 78" },
];

export default function Memory() {
  const [prefs, setPrefs] = useState([]);
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [drafts, setDrafts] = useState({ taille: { key: "", value: "" }, habitude: { key: "", value: "" } });
  const [addr, setAddr] = useState({ label: "", full_name: "", line1: "", line2: "", zip_code: "", city: "", country: "France", phone: "" });
  const [newFact, setNewFact] = useState("");

  const load = () => {
    api.get("/preferences").then(({ data }) => setPrefs(data.preferences || [])).catch(() => {});
    api.get("/memory").then(({ data }) => setFacts(data || [])).catch(() => {});
  };

  useEffect(() => {
    Promise.all([
      api.get("/preferences").then(({ data }) => setPrefs(data.preferences || [])),
      api.get("/memory").then(({ data }) => setFacts(data || [])),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addPref = async (category) => {
    const d = drafts[category];
    if (!d.key.trim() || !d.value.trim()) return toast.error("Renseigne la clé et la valeur");
    setBusy(`add-${category}`);
    try {
      await api.post("/preferences", { category, key: d.key.trim(), value: d.value.trim() });
      setDrafts((prev) => ({ ...prev, [category]: { key: "", value: "" } }));
      toast.success("Préférence enregistrée");
      load();
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible"));
    } finally {
      setBusy(null);
    }
  };

  const addAddress = async () => {
    if (!addr.label.trim() || !addr.line1.trim() || !addr.city.trim()) {
      return toast.error("Nom de l'adresse, rue et ville sont nécessaires");
    }
    const { label, ...value } = addr;
    setBusy("add-adresse");
    try {
      await api.post("/preferences", { category: "adresse", key: label.trim(), value });
      setAddr({ label: "", full_name: "", line1: "", line2: "", zip_code: "", city: "", country: "France", phone: "" });
      toast.success("Adresse enregistrée");
      load();
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible"));
    } finally {
      setBusy(null);
    }
  };

  const setDefault = async (id) => {
    try {
      await api.post(`/preferences/${id}/default`);
      toast.success("Adresse de livraison par défaut");
      load();
    } catch (e) {
      toast.error(apiError(e, "Échec"));
    }
  };

  const removePref = async (id) => {
    setPrefs((prev) => prev.filter((p) => p.id !== id));
    try {
      await api.delete(`/preferences/${id}`);
    } catch (e) {
      toast.error("Suppression impossible");
      load();
    }
  };

  const addFact = async (e) => {
    e.preventDefault();
    if (!newFact.trim()) return;
    setBusy("fact");
    try {
      const { data } = await api.post("/memory", { fact: newFact.trim() });
      setFacts((prev) => [data, ...prev]);
      setNewFact("");
      toast.success("Souvenir ajouté");
    } catch (e) {
      toast.error(apiError(e, "Ajout impossible"));
    } finally {
      setBusy(null);
    }
  };

  const removeFact = async (id) => {
    setFacts((prev) => prev.filter((f) => f.id !== id));
    try {
      await api.delete(`/memory/${id}`);
    } catch (_) {
      toast.error("Suppression impossible");
    }
  };

  const byCat = (c) => prefs.filter((p) => p.category === c);

  return (
    <AppShell title="Mémoire" subtitle="Ce que Sara retient de vous — tailles, adresses, habitudes et souvenirs">
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin accent-text" />
        </div>
      ) : (
        <div className="space-y-6" data-testid="memory-content">
          <p className="clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.6)] p-3.5 font-mono-hud text-[11px] leading-relaxed text-[#8b949e]">
            <Sparkles className="mr-1.5 inline h-3.5 w-3.5 accent-text" />
            Sara enregistre automatiquement ces informations quand vous lui parlez (« je fais du M », « livre au
            bureau »). Elle les réutilise pour remplir des formulaires, choisir une taille ou livrer à la bonne adresse.
          </p>

          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
            {CATEGORIES.map((c) => (
              <section key={c.id} data-testid={`pref-card-${c.id}`} className="hud-panel reticle clip-corner p-5">
                <span className="rc-tr" />
                <span className="rc-bl" />
                <div className="mb-4 flex items-center gap-2">
                  <c.icon className="h-4 w-4 accent-text" />
                  <h3 className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-[#8b949e]">{c.label}</h3>
                  <span className="ml-auto font-mono-hud text-[10px] text-[#6b7a8d]">{byCat(c.id).length}</span>
                </div>

                <ul className="mb-4 space-y-1.5">
                  {byCat(c.id).map((p) => (
                    <li
                      key={p.id}
                      data-testid={`pref-item-${p.id}`}
                      className="flex items-center gap-2 clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.55)] px-3 py-2"
                    >
                      <span className="font-mono-hud text-[10px] uppercase tracking-widest accent-text">{p.key}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-[#e1e9f6]">{p.display}</span>
                      {p.source === "chat" && (
                        <span className="shrink-0 font-mono-hud text-[9px] uppercase tracking-widest text-[#6b7a8d]">vocal</span>
                      )}
                      <button
                        type="button"
                        data-testid={`pref-delete-${p.id}`}
                        onClick={() => removePref(p.id)}
                        className="shrink-0 text-[#6b7a8d] transition-colors hover:text-[#ff6b6b]"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                  {byCat(c.id).length === 0 && (
                    <li className="font-mono-hud text-[10px] uppercase tracking-widest text-[#6b7a8d]">Rien de mémorisé</li>
                  )}
                </ul>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    data-testid={`pref-key-input-${c.id}`}
                    value={drafts[c.id].key}
                    onChange={(e) => setDrafts((p) => ({ ...p, [c.id]: { ...p[c.id], key: e.target.value } }))}
                    placeholder="Clé (ex. pointure)"
                    className="hud-input sm:w-40"
                  />
                  <input
                    data-testid={`pref-value-input-${c.id}`}
                    value={drafts[c.id].value}
                    onChange={(e) => setDrafts((p) => ({ ...p, [c.id]: { ...p[c.id], value: e.target.value } }))}
                    placeholder="Valeur (ex. 42)"
                    className="hud-input flex-1"
                  />
                  <button
                    type="button"
                    data-testid={`pref-add-${c.id}`}
                    onClick={() => addPref(c.id)}
                    disabled={busy === `add-${c.id}`}
                    className="hud-btn clip-corner flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-bold disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" /> Ajouter
                  </button>
                </div>
                <p className="mt-2 font-mono-hud text-[9px] text-[#6b7a8d]">{c.hint}</p>
              </section>
            ))}
          </div>

          {/* Addresses */}
          <section data-testid="pref-card-adresse" className="hud-panel reticle clip-corner p-5">
            <span className="rc-tr" />
            <span className="rc-bl" />
            <div className="mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4 accent-text" />
              <h3 className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-[#8b949e]">Adresses de livraison</h3>
              <span className="ml-auto font-mono-hud text-[10px] text-[#6b7a8d]">{byCat("adresse").length}</span>
            </div>

            <ul className="mb-5 grid gap-2 sm:grid-cols-2">
              {byCat("adresse").map((p) => (
                <li
                  key={p.id}
                  data-testid={`pref-item-${p.id}`}
                  className={`clip-corner border p-3 ${
                    p.is_default ? "border-[var(--accent)] bg-[var(--accent-a10)]" : "border-[var(--border-hud)] bg-[rgba(2,8,18,0.55)]"
                  }`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="font-mono-hud text-[10px] uppercase tracking-widest accent-text">{p.key}</span>
                    {p.is_default && (
                      <span className="font-mono-hud text-[9px] uppercase tracking-widest text-[#00ffaa]">par défaut</span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                      {!p.is_default && (
                        <button
                          type="button"
                          data-testid={`pref-default-${p.id}`}
                          onClick={() => setDefault(p.id)}
                          className="text-[#6b7a8d] transition-colors hover:accent-text"
                          title="Définir par défaut"
                        >
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`pref-delete-${p.id}`}
                        onClick={() => removePref(p.id)}
                        className="text-[#6b7a8d] transition-colors hover:text-[#ff6b6b]"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm leading-snug text-[#e1e9f6]">{p.display}</p>
                </li>
              ))}
              {byCat("adresse").length === 0 && (
                <li className="font-mono-hud text-[10px] uppercase tracking-widest text-[#6b7a8d]">Aucune adresse</li>
              )}
            </ul>

            <div className="grid gap-3 sm:grid-cols-4">
              <label className="block sm:col-span-1">
                <span className="mb-1.5 block font-mono-hud text-[9px] uppercase tracking-[0.2em] text-[#8b949e]">Nom de l'adresse</span>
                <input
                  data-testid="addr-label-input"
                  value={addr.label}
                  onChange={(e) => setAddr((a) => ({ ...a, label: e.target.value }))}
                  placeholder="domicile / bureau"
                  className="hud-input"
                />
              </label>
              {ADDRESS_FIELDS.map((f) => (
                <label key={f.id} className="block">
                  <span className="mb-1.5 block font-mono-hud text-[9px] uppercase tracking-[0.2em] text-[#8b949e]">{f.label}</span>
                  <input
                    data-testid={`addr-${f.id}-input`}
                    value={addr[f.id]}
                    onChange={(e) => setAddr((a) => ({ ...a, [f.id]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="hud-input"
                  />
                </label>
              ))}
            </div>
            <button
              type="button"
              data-testid="addr-add-button"
              onClick={addAddress}
              disabled={busy === "add-adresse"}
              className="hud-btn clip-corner mt-4 flex items-center gap-2 px-4 py-2.5 text-[12px] font-bold disabled:opacity-50"
            >
              {busy === "add-adresse" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Ajouter l'adresse
            </button>
          </section>

          {/* Long-term facts */}
          <section data-testid="memory-facts-section" className="hud-panel clip-corner p-5">
            <div className="mb-4 flex items-center gap-2">
              <BrainCircuit className="h-4 w-4 accent-text" />
              <h3 className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-[#8b949e]">Souvenirs à long terme</h3>
              <span className="ml-auto font-mono-hud text-[10px] text-[#6b7a8d]">{facts.length}</span>
            </div>

            <form onSubmit={addFact} className="mb-4 flex flex-col gap-2 sm:flex-row">
              <input
                data-testid="memory-fact-input"
                value={newFact}
                onChange={(e) => setNewFact(e.target.value)}
                placeholder="Ex. Travaille sur un projet de domotique"
                className="hud-input flex-1"
              />
              <button
                type="submit"
                data-testid="memory-fact-add"
                disabled={busy === "fact"}
                className="hud-btn clip-corner flex items-center justify-center gap-1.5 px-4 py-2.5 text-[11px] font-bold disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" /> Mémoriser
              </button>
            </form>

            <ul className="space-y-1.5">
              {facts.map((f) => (
                <li
                  key={f.id}
                  data-testid={`memory-fact-${f.id}`}
                  className="flex items-start gap-2 clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.55)] px-3 py-2"
                >
                  <span className="min-w-0 flex-1 text-sm leading-snug text-[#e1e9f6]">{f.fact}</span>
                  <button
                    type="button"
                    data-testid={`memory-fact-delete-${f.id}`}
                    onClick={() => removeFact(f.id)}
                    className="mt-0.5 shrink-0 text-[#6b7a8d] transition-colors hover:text-[#ff6b6b]"
                    title="Oublier"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
              {facts.length === 0 && (
                <li className="font-mono-hud text-[10px] uppercase tracking-widest text-[#6b7a8d]">Aucun souvenir pour l'instant</li>
              )}
            </ul>
          </section>
        </div>
      )}
    </AppShell>
  );
}
